import { DEFAULT_AVATAR, DEFAULT_CONFIG, type Avatar, type GameConfig } from '../../shared/config';
import { MODE_CONFIG, type GameMode } from '../../shared/modes';
import type {
  ChatMessage,
  EndlessBoard,
  PlayerView,
  ReadyCheckView,
  StateMessage,
} from '../../shared/protocol';
import type { ActiveEffect, PowerUpSpawn } from '../../shared/power-ups';
import type { ResultDetail } from '../../shared/api';
import type { ProfileBlockFeatures } from '../../shared/profile-blocks';
import type { RoundResults, RoundPlayerResult, WordView } from '../../shared/results';
import type { Submission } from './submissions';
import type { DecodedSolutionFilter } from '../../shared/solution-filter';

export interface SnapshotState {
  archivedResult: ResultDetail | null;
  resultLifecycle: StateMessage['resultLifecycle'];
  mode: GameMode;
  dailyDate: string | null;
  spectator: boolean;
  finished: boolean;
  /** Async pace: this browser's participant has started their own turn. */
  begun: boolean;
  phase: StateMessage['phase'];
  /** Rounds started in this room so far. */
  round: number;
  roundId: string | null;
  players: PlayerView[];
  /** Lobby chat: authoritative snapshot replaces, live `chat` messages append. */
  chat: ChatMessage[];
  meId: string;
  startAt: number;
  endAt: number;
  config: GameConfig;
  activeConfig: GameConfig | null;
  board: string[] | null;
  results: RoundResults | null;
  /** Live Versus shared reveal start; null when no gated recap is active. */
  revealAt: number | null;
  /** Live Versus: the host's start is waiting for guests to confirm; null otherwise. */
  readyCheck: ReadyCheckView | null;
  found: Map<string, number>;
  /** Chaos rounds: the round's spawn schedule, consumed entries marked `takenBy`; empty otherwise. */
  powerUps: PowerUpSpawn[];
  /** Chaos rounds: timed effects on this browser's participant and whether it holds a shield. */
  effects: ActiveEffect[];
  shield: boolean;
  /** While this browser may enter words: the round's solution filter for instant local verdicts. */
  solutionFilter: DecodedSolutionFilter | null;
  /** Endless rounds in play: the board number and its replacement time; null otherwise. */
  endlessBoard: EndlessBoard | null;
}

export interface FormState {
  roomInput: string;
  /** The mode of the room this browser is entering, or last entered. */
  selectedMode: GameMode;
  /** The Host form's Versus settings; other modes are created from their `MODE_CONFIG` entry. */
  hostConfig: GameConfig;
}

/** The profile this browser plays as: its credential and the cached server-owned identity. */
export interface IdentityState {
  roomToken: string | null;
  profileCredential: string | null;
  profileGeneration: number;
  /** Empty until onboarding; the welcome dialog gates on it. */
  name: string;
  avatar: Avatar;
  /** False while a local edit has not yet been written to the profile. */
  profileSynced: boolean;
  /** Server-owned future epoch milliseconds while play is disabled. */
  blockedUntil: number | null;
  blockMessage: string | null;
  /** NULL restricts the whole site; [] deliberately restricts nothing. */
  blockedFeatures: ProfileBlockFeatures | null;
}

export interface WorkflowState {
  archivePendingRoundId: string | null;
  autoStart: boolean;
  /** Presenting an unstarted solo/Daily room's setup lobby (see the ARCHITECTURE glossary). */
  inSetupLobby: boolean;
  /** Turn-taking rooms: the last results this browser dismissed, so revisits skip them. */
  dismissedResultsRoundId: string | null;
  roomCode: string;
  joinStatus: string;
  creatingRoom: boolean;
  joiningRoom: boolean;
  recordedResult: string;
  connected: boolean;
  joined: boolean;
  ready: boolean;
  hasSnapshot: boolean;
  reconnectStopped: boolean;
  serverOffset: number;
  submissions: Submission[];
  hasRoomInfo: boolean;
}

export function initialSessionState(): {
  snapshot: SnapshotState;
  form: FormState;
  identity: IdentityState;
  workflow: WorkflowState;
} {
  return {
    snapshot: {
      archivedResult: null,
      resultLifecycle: { continuation: 'continue-room', archiveRoundId: null },
      mode: 'versus',
      dailyDate: null,
      spectator: false,
      finished: false,
      begun: false,
      phase: 'lobby',
      round: 0,
      roundId: null,
      players: [],
      chat: [],
      meId: '',
      startAt: 0,
      endAt: 0,
      config: { ...DEFAULT_CONFIG },
      activeConfig: null,
      board: null,
      results: null,
      revealAt: null,
      readyCheck: null,
      found: new Map<string, number>(),
      solutionFilter: null,
      endlessBoard: null,
      powerUps: [],
      effects: [],
      shield: false,
    },
    form: {
      roomInput: '',
      selectedMode: 'versus',
      hostConfig: { ...DEFAULT_CONFIG },
    },
    identity: {
      roomToken: null,
      profileCredential: null,
      profileGeneration: 0,
      name: '',
      avatar: DEFAULT_AVATAR,
      profileSynced: true,
      blockedUntil: null,
      blockMessage: null,
      blockedFeatures: null,
    },
    workflow: {
      archivePendingRoundId: null,
      autoStart: false,
      inSetupLobby: false,
      dismissedResultsRoundId: null,
      roomCode: '',
      joinStatus: '',
      creatingRoom: false,
      joiningRoom: false,
      recordedResult: '',
      connected: false,
      joined: false,
      ready: false,
      hasSnapshot: false,
      reconnectStopped: false,
      serverOffset: 0,
      submissions: [],
      hasRoomInfo: false,
    },
  };
}

export type SessionRoundResults = Readonly<Omit<RoundResults, 'config' | 'players' | 'missed' | 'board'>> & {
  readonly config: Readonly<GameConfig>;
  readonly players: readonly (Readonly<Omit<RoundPlayerResult, 'words'>> & {
    readonly words: readonly Readonly<WordView>[];
  })[];
  readonly missed: readonly Readonly<WordView>[];
  readonly board: readonly string[];
};
export type SessionArchivedResult = Readonly<
  Omit<ResultDetail, 'config' | 'words' | 'board' | 'players' | 'missed'>
> & {
  readonly config: Readonly<GameConfig>;
  readonly words: readonly Readonly<WordView>[];
  readonly board: readonly string[];
  readonly players: SessionRoundResults['players'];
  readonly missed: SessionRoundResults['missed'] | null;
};

/** A single live read projection; never clone/freeze server data for notifications. */
export function sessionView(
  snapshot: SnapshotState,
  form: FormState,
  workflow: WorkflowState,
  identity: IdentityState,
) {
  return {
    get resultLifecycle(): Readonly<StateMessage['resultLifecycle']> {
      return snapshot.resultLifecycle;
    },
    get mode(): GameMode {
      return workflow.hasRoomInfo ? snapshot.mode : form.selectedMode;
    },
    get dailyDate(): string | null {
      return workflow.hasRoomInfo ? snapshot.dailyDate : null;
    },
    get finished(): boolean {
      return snapshot.finished;
    },
    get begun(): boolean {
      return snapshot.begun;
    },
    get spectator(): boolean {
      return snapshot.spectator;
    },
    get phase(): StateMessage['phase'] {
      return snapshot.phase;
    },
    get round(): number {
      return snapshot.round;
    },
    get roundId(): string | null {
      return snapshot.roundId;
    },
    get players(): readonly Readonly<PlayerView>[] {
      return snapshot.players;
    },
    get chat(): readonly Readonly<ChatMessage>[] {
      return snapshot.chat;
    },
    get meId(): string {
      return snapshot.meId;
    },
    get startAt(): number {
      return snapshot.startAt;
    },
    get endAt(): number {
      return snapshot.endAt;
    },
    get config(): Readonly<GameConfig> {
      if (workflow.hasRoomInfo) return snapshot.config;
      return form.selectedMode === 'versus' ? form.hostConfig : MODE_CONFIG[form.selectedMode];
    },
    get activeConfig(): Readonly<GameConfig> | null {
      return snapshot.activeConfig;
    },
    get board(): readonly string[] | null {
      return snapshot.board;
    },
    get results(): SessionRoundResults | null {
      return snapshot.results;
    },
    get revealAt(): number | null {
      return snapshot.revealAt;
    },
    get readyCheck(): Readonly<ReadyCheckView> | null {
      return snapshot.readyCheck;
    },
    get found(): ReadonlyMap<string, number> {
      return snapshot.found;
    },
    get powerUps(): readonly Readonly<PowerUpSpawn>[] {
      return snapshot.powerUps;
    },
    get effects(): readonly Readonly<ActiveEffect>[] {
      return snapshot.effects;
    },
    get shield(): boolean {
      return snapshot.shield;
    },
    get solutionFilter(): DecodedSolutionFilter | null {
      return snapshot.solutionFilter;
    },
    get endlessBoard(): Readonly<EndlessBoard> | null {
      return snapshot.endlessBoard;
    },
    get roomInput(): string {
      return form.roomInput;
    },
    get name(): string {
      return identity.name;
    },
    get avatar(): Avatar {
      return identity.avatar;
    },
    get profileCredential(): string | null {
      return identity.profileCredential;
    },
    get profileGeneration(): number {
      return identity.profileGeneration;
    },
    get blockedUntil(): number | null {
      return identity.blockedUntil;
    },
    get blockMessage(): string | null {
      return identity.blockMessage;
    },
    get blockedFeatures(): Readonly<ProfileBlockFeatures> | null {
      return identity.blockedFeatures;
    },
    get archivePendingRoundId(): string | null {
      return workflow.archivePendingRoundId;
    },
    get archivedResult(): SessionArchivedResult | null {
      return snapshot.archivedResult;
    },
    /** Room state to release when navigation leaves the room: entry, membership or its archive. */
    get holdsRoom(): boolean {
      return (
        workflow.joined ||
        workflow.inSetupLobby ||
        workflow.creatingRoom ||
        workflow.joiningRoom ||
        workflow.archivePendingRoundId !== null ||
        snapshot.archivedResult !== null
      );
    },
    get autoStart(): boolean {
      return workflow.autoStart;
    },
    get inSetupLobby(): boolean {
      return workflow.inSetupLobby;
    },
    get dismissedResultsRoundId(): string | null {
      return workflow.dismissedResultsRoundId;
    },
    get roomCode(): string {
      return workflow.roomCode;
    },
    get joinStatus(): string {
      return workflow.joinStatus;
    },
    get creatingRoom(): boolean {
      return workflow.creatingRoom;
    },
    get joiningRoom(): boolean {
      return workflow.joiningRoom;
    },
    get connected(): boolean {
      return workflow.connected;
    },
    get joined(): boolean {
      return workflow.joined;
    },
    get ready(): boolean {
      return workflow.ready;
    },
    get hasSnapshot(): boolean {
      return workflow.hasSnapshot;
    },
    get reconnectStopped(): boolean {
      return workflow.reconnectStopped;
    },
    get serverOffset(): number {
      return workflow.serverOffset;
    },
    get submissions(): readonly Readonly<Submission>[] {
      return workflow.submissions;
    },
    get hostConfig(): Readonly<GameConfig> {
      return form.hostConfig;
    },
  };
}
export type RoomSessionView = ReturnType<typeof sessionView>;
